"use client";

import React, { useState, useCallback } from "react";
import clsx from "clsx";
import { debounce } from "es-toolkit/function";

import { useWindowSize } from "usehooks-ts";
import Atom from "../Atom";
import { AtomProps } from "../Atom/types";
import NavigationArrow from "./NavigationArrow";

const round = (num: number) => {
  return num - Math.round(num) > 0.1 ? Math.ceil(num) : Math.round(num);
};

type CarouselRow = Omit<AtomProps, "className">;

type CarouselProps = {
  scrollByOneSlide?: boolean;
  rows?: CarouselRow[];
};

const CarouselRows = ({ scrollByOneSlide = false, rows = [] }: CarouselProps) => {
  const windowSize = useWindowSize();

  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [activeDot, setActiveDot] = useState(0);
  const [dotsCount, setDotsCounts] = useState(0);

  const itemsLength = rows?.length;

  const ref = useCallback(
    (node: HTMLDivElement) => {
      if (node) {
        setNode(node);

        if (scrollByOneSlide) {
          setDotsCounts(itemsLength);
        } else {
          setDotsCounts(round(node.scrollWidth / windowSize.width));
        }
      }
    },
    [itemsLength, scrollByOneSlide, windowSize.width]
  );

  const onPrevClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (node) {
        let left = node.scrollLeft - windowSize.width;

        if (scrollByOneSlide) {
          left = node.scrollLeft - windowSize.width / itemsLength;
        }

        node.scroll({
          left,
          top: 0,
          behavior: "smooth"
        });
      }
    },
    [node, windowSize.width, scrollByOneSlide, itemsLength]
  );

  const onNextClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (node) {
        let left = node.scrollLeft + windowSize.width;

        if (scrollByOneSlide) {
          if (node.scrollLeft + windowSize.width / itemsLength >= windowSize.width) {
            left = windowSize.width + windowSize.width / itemsLength;
          } else {
            left = node.scrollLeft + windowSize.width / itemsLength;
          }
        }

        node.scroll({
          left,
          top: 0,
          behavior: "smooth"
        });
      }
    },
    [node, windowSize.width, scrollByOneSlide, itemsLength]
  );

  const onScroll = useCallback(
    debounce(() => {
      if (node) {
        const { scrollLeft } = node;

        if (scrollByOneSlide) {
          const slideWidth = windowSize.width / itemsLength;
          const slideIndex = Math.floor(scrollLeft / slideWidth);

          setActiveDot(slideIndex > itemsLength - 1 ? itemsLength - 1 : slideIndex);
        } else {
          setActiveDot(round(scrollLeft / windowSize.width));
        }
      }
    }, 25),
    [node, windowSize.width]
  );

  const onDotClick = useCallback(
    (dot: number) => {
      setActiveDot(dot);

      let left = dot * windowSize.width;

      if (scrollByOneSlide) {
        if (dot === 0) {
          left = 0;
        } else if (dot === dotsCount - 1) {
          left = windowSize.width;
        } else {
          left = dot * (windowSize.width / itemsLength);
        }
      }

      node?.scroll({
        left,
        top: 0,
        behavior: "smooth"
      });
    },
    [dotsCount, itemsLength, node, scrollByOneSlide, windowSize.width]
  );

  if (!rows?.length) return null;

  return (
    <div className="group/carousel relative">
      <div
        ref={ref}
        onScroll={onScroll}
        className="flex snap-x snap-mandatory items-center overflow-x-auto overflow-y-hidden scrollbar-hide"
      >
        {rows.map((item, i) => (
          <div key={i} className="relative flex h-full snap-start justify-center">
            {activeDot !== i ? (
              <button
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  activeDot < i && onNextClick(e);
                  activeDot > i && onPrevClick(e);
                }}
              />
            ) : null}
            {activeDot === i && <div />}
            <div className="flex h-full min-h-[530px] w-[80vw] flex-col px-1.5 pb-5 sm:w-[50vw] lg:w-[33vw] xl:w-[25vw] 2xl:w-[20vw]">
              <Atom
                {...item}
                assetType="image"
                bgColor="#FFFDF6"
                mediaHeight={item.mediaHeight}
                isLeftAlign
                className="flex-1"
              />
            </div>
          </div>
        ))}
      </div>
      {activeDot > 0 && <NavigationArrow className="hidden lg:flex" direction="left" onClick={onPrevClick} />}
      {activeDot < dotsCount - 1 && (
        <NavigationArrow className="hidden lg:flex" direction="right" onClick={onNextClick} />
      )}
      {dotsCount > 0 && (
        <ul className="list-style-none absolute bottom-1 flex w-full justify-center text-center">
          {[...Array(dotsCount).keys()].map((dot) => (
            <li key={dot} className="relative inline-block h-5 w-5 cursor-pointer">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDotClick(dot);
                }}
                className={clsx(
                  "block h-5 w-5 cursor-pointer border-0 bg-transparent p-0.5 text-transparent outline-none",
                  "before:text-5xl before:absolute before:left-0 before:top-0 before:h-5 before:w-5 before:text-center before:leading-5 before:content-['•']",
                  activeDot === dot ? "before:text-primary-700" : "before:text-neutral-200"
                )}
              >
                {dot}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CarouselRows;
